import {Card, RadioItem} from 'platform/components';
import {Box, Clickable, Heading, HStack, Show, Text, VStack} from 'platform/foundation';

import {ReactNode, useId} from 'react';

import {isNotNil} from 'ramda';

import {TestIdProps, useBoolean} from 'shared/web';

export interface RadioCardProps extends TestIdProps {
  isSelected: boolean;
  onClick: VoidFunction;
  title: string;
  description?: string;
  illustration?: ReactNode;
  name?: string;
}

export function RadioCard(props: RadioCardProps) {
  const inputId = useId();
  const [isFocused, setFocused, setBlurred] = useBoolean();
  const hasRichContent = isNotNil(props.illustration) || isNotNil(props.description);

  const radioItem = (
    <RadioItem
      name={props.name}
      value={props.isSelected}
      externalLabelId={inputId}
      onChange={props.onClick}
      onFocus={setFocused}
      onBlur={setBlurred}
      data-testid={props['data-testid']}
    />
  );

  const title = (
    <label htmlFor={inputId}>
      <Heading size={4}>{props.title}</Heading>
    </label>
  );

  if (!hasRichContent) {
    return (
      // The radio input is visually hidden, so the card itself carries the focus ring.
      <Box borderRadius="small" boxShadow={isFocused ? 'active' : undefined}>
        <Card variant="inlineGrey">
          <Clickable onClick={props.onClick} data-testid={props['data-testid']}>
            <HStack spacing={4}>
              {radioItem}
              {title}
            </HStack>
          </Clickable>
        </Card>
      </Box>
    );
  }

  return (
    <Box flex={1}>
      <Clickable height="100%" onClick={props.onClick} data-testid={props['data-testid']}>
        <Box
          border="1px solid"
          borderColor={props.isSelected ? 'general.accent' : 'general.separator'}
          borderRadius="medium"
          boxShadow={isFocused ? 'active' : undefined}
          backgroundColor="general.white"
          padding={4}
          height="100%"
        >
          <VStack spacing={4} height="100%">
            <Show when={props.illustration}>{props.illustration}</Show>
            <HStack spacing={4} justify="space-between" align="center">
              {title}
              {radioItem}
            </HStack>
            <Show when={props.description}>
              <Text size="small" color="secondary">
                {props.description}
              </Text>
            </Show>
          </VStack>
        </Box>
      </Clickable>
    </Box>
  );
}
